import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import Nav from "@/components/welp/Nav";
import PageBg from "@/components/welp/PageBg";
import { AdminMessagePanel } from "@/components/welp/AdminMessagePanel";
import { AdminContactInbox } from "@/components/welp/AdminContactInbox";
import { AdminFeedbackInbox } from "@/components/welp/AdminFeedbackInbox";
import { TrendingUp, AlertTriangle, Activity, Users, Search, MessageSquare, DollarSign, FileText, Star } from "lucide-react";

const WINDOWS = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "all", label: "All time" },
];

const FUNNEL_ROWS = [
  { key: "visitors", label: "Visitors" },
  { key: "signup_completed", label: "Sign-ups" },
  { key: "search_completed", label: "Searches" },
  { key: "review_started", label: "Reviews started" },
  { key: "review_completed", label: "Reviews completed" },
  { key: "reviewed_person_notification_sent", label: "Notifications sent" },
  { key: "response_completed", label: "Responses" },
];

const EVENT_LABEL = {
  signup_completed: "New member signed up",
  review_completed: "Review completed",
  response_completed: "Response submitted",
  reviewed_person_notification_failed: "SMS notification FAILED",
  payment_completed: "Tarot reading paid",
};

function ChangeRequestsPanel() {
  const [items, setItems] = useState([]);
  const load = () => api.listChangeRequests().then(r => setItems(r.requests || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const open = items.filter(i => i.status === "open");
  if (open.length === 0) return null;
  return (
    <div className="mt-8 rounded-3xl bg-white/70 backdrop-blur-lg border-2 border-[#10B981]/40 p-5 sm:p-6" data-testid="admin-change-requests">
      <h2 className="font-heading text-2xl text-[#1A1A1A]">Change Requests ({open.length})</h2>
      <div className="mt-4 space-y-3">
        {open.map(i => (
          <div key={i.req_id} className="rounded-2xl border border-black/10 bg-white/70 p-4 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-lg font-bold text-[#FF1A82]">{i.path} · {new Date(i.created_at).toLocaleString()}</div>
              <p className="text-lg text-[#1A1A1A] font-semibold mt-1 break-words">{i.note}</p>
            </div>
            <button onClick={() => api.completeChangeRequest(i.req_id).then(load)}
              className="shrink-0 px-4 py-2 rounded-full bg-[#FF1A82] text-white font-bold text-lg hover:brightness-110"
              data-testid={`chg-done-${i.req_id}`}>Done</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SmsStatusCard() {
  const [h, setH] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.adminHealth().then(setH).catch(() => setErr(true)); }, []);
  const sms = h?.checks?.textbelt_sms || "";
  const low = /LOW|OUT/i.test(sms);
  return (
    <div className="mt-8 rounded-3xl border-2 p-5 sm:p-6 bg-white/70 backdrop-blur-lg shadow-sm"
      style={{ borderColor: low ? "#FF1A82" : "rgba(16,185,129,0.4)" }} data-testid="sms-status-card">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-5 h-5" style={{ color: low ? "#FF1A82" : "#10B981" }} />
        <h2 className="font-heading text-2xl text-[#1A1A1A]">Texting (SMS) status</h2>
      </div>
      {err && <p className="mt-3 text-lg font-bold text-[#FF1A82]">Couldn't load SMS status.</p>}
      {!h && !err && <p className="mt-3 text-lg text-[#5A5A5A]">Checking Textbelt…</p>}
      {h && (
        <div className="mt-3 flex flex-wrap gap-3">
          <span className={`px-4 py-2 rounded-full text-lg font-bold border-2 ${low ? "border-[#FF1A82]/50 bg-[#FF1A82]/10 text-[#FF1A82]" : "border-[#10B981]/50 bg-[#10B981]/10 text-[#10B981]"}`}
            data-testid="sms-credits">{low ? "▲" : "●"} Credits: {sms}</span>
          <span className="px-4 py-2 rounded-full text-lg font-bold border-2 border-black/15 bg-white/60 text-[#3A3A3A]"
            data-testid="sms-mode">Mode: {h.checks?.sms_mode}</span>
        </div>
      )}
      <p className="text-sm text-[#5A5A5A] mt-3">Refill at textbelt.com. Full system health is on your Account page.</p>
    </div>
  );
}

function Card({ title, icon: Icon, children, testid, accent = "#10B981" }) {
  return (
    <div className="mt-8 rounded-3xl border border-black/10 bg-white/70 backdrop-blur-lg shadow-sm p-5 sm:p-6" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5" style={{ color: accent }} />
        <h2 className="font-heading text-2xl text-[#1A1A1A]">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function FunnelBar({ label, value, max, testid }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 2;
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-lg font-semibold text-[#1A1A1A]">{label}</span>
        <span className="text-lg font-bold text-[#10B981]">{value}</span>
      </div>
      <div className="mt-1 h-3 rounded-full bg-black/10 overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-[#10B981] to-[#FF1A82]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function MetricBlock({ label, value, sub }) {
  return (
    <div className="rounded-2xl border border-black/10 bg-white/70 p-4" data-testid={`metric-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="text-sm uppercase tracking-widest text-[#5A5A5A] font-semibold">{label}</div>
      <div className="font-heading text-3xl text-[#1A1A1A] mt-1">{value}</div>
      {sub && <div className="text-sm text-[#5A5A5A] mt-1">{sub}</div>}
    </div>
  );
}

function LegacyTraffic() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const loadLegacy = () => {
    if (loaded) return;
    setLoaded(true);
    const tz = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || undefined;
    api.adminStats(tz).then(setData).catch(() => {});
  };
  return (
    <details className="mt-8 rounded-3xl border border-black/15 bg-white/50 backdrop-blur-lg p-5 sm:p-6" onToggle={(e) => e.target.open && loadLegacy()} data-testid="legacy-section">
      <summary className="cursor-pointer text-lg font-bold text-[#5A5A5A]">
        Legacy traffic data (pre-fix — unreliable) — tap to view
      </summary>
      <p className="text-lg text-[#5A5A5A] mt-3">
        Page-view data collected before the event system was fixed. Numbers here are inflated and unreliable — kept only for the record. Everything above uses the new clean event tracking.
      </p>
      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <MetricBlock label="Views 24h" value={data.totals.views_24h} sub={`${data.totals.unique_24h} unique`} />
            <MetricBlock label="Views 7d" value={data.totals.views_7d} sub={`${data.totals.unique_7d} unique`} />
            <MetricBlock label="Views 30d" value={data.totals.views_30d} sub={`${data.totals.unique_30d} unique`} />
            <MetricBlock label="All time" value={data.totals.all_time_views} sub={`${data.totals.all_time_visitors} visitors`} />
          </div>
          <button data-testid="analytics-reset-btn"
            onClick={async () => {
              if (!window.confirm("Archive ALL old traffic data and start clean? Members, reviews and payments are untouched.")) return;
              try {
                const r = await api.adminAnalyticsReset();
                toast.success(r.message || "Analytics reset — stats start clean now.");
                setTimeout(() => window.location.reload(), 1200);
              } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed."); }
            }}
            className="mt-5 px-6 py-3 rounded-full bg-[#FF1A82] text-white text-lg font-bold hover:brightness-110">
            Archive legacy traffic data
          </button>
        </>
      )}
    </details>
  );
}

export default function AdminStatsPage() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [ov, setOv] = useState(null);
  const [err, setErr] = useState(null);
  const [win, setWin] = useState("7d");
  const [mode, setMode] = useState("total_events");

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    const load = () => api.adminOverview().then(setOv).catch((e) => setErr(e?.response?.data?.detail || "Unable to load stats."));
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [user, loading, nav]);

  if (err) {
    return (
      <PageBg><Nav />
        <section className="max-w-3xl mx-auto px-5 py-16">
          <h1 className="font-heading text-4xl text-[#1A1A1A]">Admin Dashboard</h1>
          <p className="mt-4 text-lg text-[#3A3A3A]" data-testid="admin-stats-error">{err}</p>
        </section>
      </PageBg>
    );
  }
  if (!ov) {
    return (
      <PageBg><Nav />
        <section className="max-w-3xl mx-auto px-5 py-16"><p className="text-lg text-[#3A3A3A]">Loading dashboard…</p></section>
      </PageBg>
    );
  }

  const w = ov.funnel[win] || { visitors_unique: 0, events: {} };
  const rowVal = (key) => key === "visitors"
    ? w.visitors_unique
    : (w.events[key]?.[mode] || 0);
  const funnelMax = Math.max(1, ...FUNNEL_ROWS.map(r => rowVal(r.key)));

  const rf = ov.review_funnel;
  const stepMax = Math.max(1, ...Object.values(rf.step_reach || {}).map(Number), rf.opened || 0);

  return (
    <PageBg>
      <Nav />
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-testid="admin-stats-page">
        <div className="text-veil">
          <div className="text-xs uppercase tracking-[0.32em] mb-3">
            <span className="text-[#10B981]">Welp</span> <span className="text-[#5A5A5A]">·</span> Admin
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A1A]">Dashboard</h1>
          <p className="text-lg text-[#3A3A3A] mt-2">
            Clean event tracking{ov.clean_since ? ` since ${new Date(ov.clean_since).toLocaleDateString()}` : ""}. Bots and your own admin activity are excluded from every number.
          </p>
        </div>

        {/* SMS STATUS */}
        <SmsStatusCard />

        {/* ALERTS */}
        {ov.alerts?.length > 0 && (
          <Card title="Alerts" icon={AlertTriangle} accent="#FF1A82" testid="admin-alerts">
            <ul className="mt-3 space-y-2">
              {ov.alerts.map((a, i) => (
                <li key={i} className="text-lg font-semibold text-[#1A1A1A] rounded-xl bg-[#FF1A82]/10 border border-[#FF1A82]/30 px-4 py-2">{a}</li>
              ))}
            </ul>
          </Card>
        )}

        {/* MAIN FUNNEL */}
        <Card title="Member Funnel" icon={TrendingUp} testid="admin-funnel">
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {WINDOWS.map(x => (
              <button key={x.key} onClick={() => setWin(x.key)} data-testid={`funnel-window-${x.key}`}
                className={`px-4 py-2 rounded-full text-lg font-bold border-2 transition-colors ${win === x.key ? "bg-[#10B981] border-[#10B981] text-white" : "bg-white/60 border-black/15 text-[#3A3A3A]"}`}>
                {x.label}
              </button>
            ))}
          </div>
          <div className="mt-3 inline-flex rounded-full border-2 border-black/15 overflow-hidden" data-testid="funnel-mode-toggle">
            <button onClick={() => setMode("total_events")} data-testid="funnel-mode-total"
              className={`px-4 py-1.5 text-lg font-bold ${mode === "total_events" ? "bg-[#FF1A82] text-white" : "bg-white/60 text-[#3A3A3A]"}`}>Total events</button>
            <button onClick={() => setMode("unique_users")} data-testid="funnel-mode-unique"
              className={`px-4 py-1.5 text-lg font-bold ${mode === "unique_users" ? "bg-[#FF1A82] text-white" : "bg-white/60 text-[#3A3A3A]"}`}>Unique users</button>
          </div>
          <p className="text-sm text-[#5A5A5A] mt-2">Visitors are always unique devices. Other rows follow the toggle above.</p>
          <div className="mt-5 space-y-4">
            {FUNNEL_ROWS.map(r => (
              <FunnelBar key={r.key} label={r.label} value={rowVal(r.key)} max={funnelMax} testid={`funnel-row-${r.key}`} />
            ))}
          </div>
        </Card>

        {/* REVIEW FUNNEL */}
        <Card title="Review Funnel — step by step" icon={Star} accent="#FF1A82" testid="admin-review-funnel">
          <div className="grid grid-cols-3 gap-3 mt-4">
            <MetricBlock label="Opened" value={rf.opened} />
            <MetricBlock label="Started" value={rf.started} />
            <MetricBlock label="Completed" value={rf.completed} />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <MetricBlock label="Completion rate" value={rf.completion_rate != null ? `${rf.completion_rate}%` : "—"} sub="of started reviews" />
            <MetricBlock label="Biggest drop-off" value={rf.biggest_dropoff_step ? `Step ${rf.biggest_dropoff_step}` : "—"} sub="most abandoned step" />
          </div>
          <h3 className="text-lg font-bold text-[#1A1A1A] mt-6">How far people get (step 1–10)</h3>
          <div className="mt-3 space-y-3" data-testid="review-step-reach">
            {Array.from({ length: 10 }, (_, i) => String(i + 1)).map(s => (
              <FunnelBar key={s} label={`Step ${s}`} value={Number(rf.step_reach?.[s] || 0)} max={stepMax} testid={`review-step-${s}`} />
            ))}
          </div>
          {Object.keys(rf.abandoned_by_step || {}).length > 0 && (
            <>
              <h3 className="text-lg font-bold text-[#1A1A1A] mt-6">Abandoned at step</h3>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="review-abandoned-steps">
                {Object.entries(rf.abandoned_by_step).map(([s, c]) => (
                  <span key={s} className="px-3 py-1.5 rounded-full bg-[#FF1A82]/10 border border-[#FF1A82]/30 text-lg font-semibold text-[#1A1A1A]">Step {s}: {c}</span>
                ))}
              </div>
            </>
          )}
        </Card>

        {/* RECENT ACTIVITY */}
        <Card title="Recent Activity" icon={Activity} testid="admin-activity">
          {(!ov.activity || ov.activity.length === 0) ? (
            <p className="text-lg text-[#5A5A5A] mt-3">Nothing yet — activity shows here as members use the site.</p>
          ) : (
            <ul className="mt-4 space-y-2">
              {ov.activity.map((a, i) => (
                <li key={i} className="flex items-baseline justify-between gap-3 border-b border-black/5 pb-2">
                  <span className={`text-lg font-semibold ${a.event.includes("failed") ? "text-[#FF1A82]" : "text-[#1A1A1A]"}`}>{EVENT_LABEL[a.event] || a.event}</span>
                  <span className="text-sm text-[#5A5A5A] shrink-0">{new Date(a.ts).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* METRIC SECTIONS */}
        <Card title="Totals" icon={Users} testid="admin-totals">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
            <MetricBlock label="Members" value={ov.users.total} sub={`${ov.users.verified} verified · ${ov.users.new_7d} new this week`} />
            <MetricBlock label="Profiles" value={ov.profiles.total} sub={`${ov.profiles.new_7d ?? 0} new this week`} />
            <MetricBlock label="Reviews" value={ov.reviews?.total ?? 0} sub={`${ov.reviews?.new_7d ?? 0} new this week`} />
            <MetricBlock label="Responses" value={ov.responses?.total ?? 0} />
            <MetricBlock label="Search hits · 7d" value={ov.search?.found_7d ?? 0} sub={`${ov.search?.no_result_7d ?? 0} found nothing`} />
            <MetricBlock label="Revenue" value={`$${(ov.revenue.total_dollars || 0).toFixed(2)}`} sub={`${ov.revenue.paid_count} paid readings · ${ov.revenue.failed_7d} failed this week`} />
          </div>
        </Card>

        <AdminMessagePanel />
        <AdminContactInbox />
        <AdminFeedbackInbox />
        <ChangeRequestsPanel />
        <LegacyTraffic />
      </section>
    </PageBg>
  );
}
